import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { VIN_SUMMARY_FIELDS as SUMMARY_FIELDS } from './vinSummaryFields';
import {
  Car,
  Search,
  Loader2,
  AlertTriangle,
  Info,
  List,
  ChevronDown
} from 'lucide-react';


function VinDecoderCard() {
  const [vinInput, setVinInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleDecode = async (event) => {
    event.preventDefault();
    const vin = vinInput.trim();

    if (!vin) {
      setError('Enter a VIN to decode.');
      setResult(null);
      return;
    }

    if (vin.length < 5) {
      setError('VIN must have at least 5 characters.');
      setResult(null);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.get('/api/vin/decode', {
        params: { vin },
        withCredentials: true
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to decode VIN.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVinInput('');
    setResult(null);
    setError(null);
    setShowDetails(false);
  };

  const renderSummary = () => {
    if (!result?.summary || Object.keys(result.summary).length === 0) {
      return (
        <p className="text-sm text-gray-500">
          The API did not return summary fields for this VIN. Check the detailed table below.
        </p>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {SUMMARY_FIELDS.filter(field => result.summary[field.key])
          .map(field => (
            <div key={field.key} className="p-3 border rounded-lg bg-gray-50">
              <p className="text-xs uppercase tracking-wide text-gray-500">{field.label}</p>
              <p className="text-sm font-semibold text-gray-800 break-words">
                {result.summary[field.key]}
              </p>
            </div>
          ))}
      </div>
    );
  };

  const formatTimestamp = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Card className="border-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              VIN Decoder
            </CardTitle>
            <CardDescription>
              Decode truck details using the public NHTSA API.
            </CardDescription>
          </div>
          {result && (
            <Badge variant="outline" className="text-xs">
              {result.vin}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleDecode} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="vinInput">VIN</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="vinInput"
                value={vinInput}
                onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                placeholder="Ex: 1XKYD49X2LJ388544"
                maxLength={30}
                className="font-mono text-sm"
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Decode
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={loading && !result}
                >
                  Clear
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Provide a full VIN. Missing values mean the NHTSA does not have that variable on file.
            </p>
          </div>
        </form>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>
              <p className="font-medium">VIN lookup failed</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col gap-2">
              <div className="flex items-center gap-2 text-blue-800">
                <Info className="w-4 h-4" />
                <p className="text-sm font-medium">{result.message || 'Lookup completed successfully.'}</p>
              </div>
              <div className="text-xs text-blue-700 space-y-1">
                {result.searchCriteria && (
                  <p><span className="font-semibold">Criteria:</span> {result.searchCriteria}</p>
                )}
                {result.count !== undefined && (
                  <p><span className="font-semibold">Variables returned:</span> {result.count}</p>
                )}
                {result.fetchedAt && (
                  <p><span className="font-semibold">Fetched at:</span> {formatTimestamp(result.fetchedAt)}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <List className="w-4 h-4 text-gray-500" />
                <p className="text-sm font-semibold text-gray-700">Summary</p>
              </div>
              {renderSummary()}
            </div>

            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowDetails((prev) => !prev)}
                className="flex items-center gap-2"
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                />
                {showDetails ? 'Hide details' : 'View all variables'}
              </Button>

              {showDetails && (
                <div className="border rounded-lg max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Variable</th>
                        <th className="px-3 py-2 text-left">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.results || []).map((item, idx) => (
                        <tr key={`${item.variable}-${item.variableId || idx}`} className="border-t">
                          <td className="px-3 py-2 font-medium text-gray-700 w-1/3">
                            {item.variable}
                          </td>
                          <td className="px-3 py-2 font-mono text-gray-900">
                            {item.value || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default VinDecoderCard;
